import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RenderIf } from 'components/desktop/_general/Conditional/RenderIf';
import InputLockButton from 'components/desktop/_general/Input/InputExtensions/InputLockButton/InputLockButton';
import InputTrashButton from 'components/desktop/_general/Input/InputExtensions/InputTrashButton/InputTrashButton';
import RoundedInput from 'components/desktop/_general/Input/RoundedInput/RoundedInput';
import SuggestionSearchInput from 'components/desktop/_general/Input/SuggestionSearchInput/SuggestionSearchInput';
import { Characteristic } from 'enums/characteristic';
import { ValueScope } from 'enums/valueScope';
import { Optional } from 'functions/promiseExtensions';
import { getValueScopeLabel } from 'functions/valueScopeConversion';
import { usePermissions } from 'hooks/usePermissions';
import { ISelectItem } from 'interfaces/ISelectItem';
import * as NutrientList from 'types/Ingredient/Detail/NutrientList';
import { Nutrient } from 'types/Nutrient/Nutrient';
import { NutrientCategoryGroup } from 'types/Nutrient/NutrientCategoryGroup';
import * as NutrientValueList from 'types/NutrientValue/NutrientValueList';

interface IProps {
	characteristic: Characteristic | null;
	allNutrients: Nutrient[];
	ingredientNutrients: NutrientList.Type;
	baseIngredientNutrients: NutrientList.Type;
	setNutrients: (input: NutrientList.Type) => void;
}

const IngredientNutrients = (props: IProps) => {
	const { t } = useTranslation();
	const { isIngredientEditor } = usePermissions();
	const [suggestedItems, setSuggestedItems] = useState<ISelectItem[]>([]);

	const concatNutrients = (): NutrientValueList.Type => {
		const baseIngredientNutrientValues = [...props.baseIngredientNutrients];
		const ingredientNutrientValues = [...props.ingredientNutrients];

		for (const nutrientValue of ingredientNutrientValues) {
			const index = baseIngredientNutrientValues.findIndex((x) => x.id == nutrientValue.id);
			if (index >= 0) {
				baseIngredientNutrientValues.splice(index, 1);
			}
		}

		return baseIngredientNutrientValues.concat(ingredientNutrientValues);
	};

	const handleNutrientValueChange = (nutrientId: string, value: number): void => {
		const newNutrients = props.ingredientNutrients.map((e) => {
			if (e.id === nutrientId) {
				return { ...e, value: value };
			}
			return { ...e };
		});
		props.setNutrients(newNutrients);
	};

	const getNutrientFromPropsWithValueAndSource = (
		id: string,
		value: number,
		source: string | null
	): Optional<Nutrient> => {
		const nutrient = getNutrientFromProps(id);
		return Optional.Maybe(nutrient).map((n) => {
			return { ...n, value, source };
		});
	};

	const getNutrientCategoryGroups = (): NutrientCategoryGroup[] => {
		return concatNutrients()
			.keepOptional((x) => getNutrientFromPropsWithValueAndSource(x.id, x.value, x.source))
			.groupBy((x) => x.nutrientCategory.id)
			.map((k: string, b: Nutrient[]) => {
				return {
					id: k,
					name: b[0].nutrientCategory.name,
					sortOrder: b[0].nutrientCategory.sortOrder,
					nutrients: b.orderBy((x) => x.sortOrder),
				};
			})
			.orderBy((x) => [x.sortOrder, x.name]);
	};

	const getNutrientFromProps = (nutrientId: string): Nutrient | null => {
		const nutrient: Nutrient | undefined = props.allNutrients.find((nutrient: Nutrient) => {
			return nutrient.id === nutrientId;
		});
		return nutrient ? nutrient : null;
	};

	const addNutrientToIngredient = (savedItem: ISelectItem): void => {
		const nutrient = getNutrientFromProps(savedItem.id);
		if (!nutrient) return;
		const newNutrients = [
			...props.ingredientNutrients,
			{
				id: nutrient.id,
				value: 0,
				source: nutrient.source,
			},
		];
		props.setNutrients(newNutrients);
	};

	const removeNutrientFromIngredient = (nutrientId: string): void => {
		const newNutrients = props.ingredientNutrients.filter((x) => x.id !== nutrientId);
		props.setNutrients(newNutrients);
	};

	const onLockClick = (nutrientId: string, isLocked: boolean): void => {
		if (isLocked) {
			const newNutrients = props.ingredientNutrients.filter((x) => x.id !== nutrientId);
			props.setNutrients(newNutrients);
		} else {
			const newNutrient = props.baseIngredientNutrients.find((e) => e.id === nutrientId);
			if (!newNutrient) return;
			props.setNutrients([...props.ingredientNutrients, newNutrient]);
		}
	};

	const calculateSuggestedItems = (searchTerm: string): ISelectItem[] => {
		const matchingNutrients: Nutrient[] = props.allNutrients.filter((nutrient: Nutrient) => {
			const inBaseIngredient: boolean = Boolean(
				props.baseIngredientNutrients.find((e) => e.id === nutrient.id)
			);
			const inIngredient: boolean = Boolean(
				props.ingredientNutrients.find((e) => e.id === nutrient.id)
			);
			if (inBaseIngredient || inIngredient) return false;
			if (searchTerm) {
				const name = nutrient.name.toLowerCase();
				searchTerm = searchTerm.toLowerCase();
				return name.startsWith(searchTerm);
			} else {
				return true;
			}
		});

		return matchingNutrients.map((nutrient: Nutrient) => {
			return {
				id: nutrient.id,
				name: `${nutrient.name} (${nutrient.nutrientCategory.name})`,
			};
		});
	};

	const handleChangeSearchTerm = (input: string): void => {
		setSuggestedItems(calculateSuggestedItems(input));
	};

	const renderInputNutrientValues = (nutrient: Nutrient): JSX.Element => {
		const inBaseIngredient: boolean = Boolean(
			props.baseIngredientNutrients.find((e) => e.id === nutrient.id)
		);
		const inIngredient: boolean = Boolean(
			props.ingredientNutrients.find((e) => e.id === nutrient.id)
		);

		return (
			<>
				<label>{`${nutrient.name} (${nutrient.unitShortNameSingular})`}</label>
				<div className="input-group">
					<RoundedInput
						disabled={!isIngredientEditor || (inBaseIngredient && !inIngredient)}
						error-key="NutrientValues"
						handleChange={(input) => handleNutrientValueChange(nutrient.id, input)}
						name={nutrient.name}
						value={nutrient.value}
					/>
					<RenderIf condition={!inBaseIngredient && inIngredient}>
						<InputTrashButton
							id={nutrient.id}
							onTrashCanClick={removeNutrientFromIngredient}
						/>
					</RenderIf>
					<RenderIf condition={inBaseIngredient && !inIngredient}>
						<InputLockButton
							isLocked={inBaseIngredient && !inIngredient}
							onLockClick={(isLocked) => onLockClick(nutrient.id, isLocked)}
						/>
					</RenderIf>
				</div>
			</>
		);
	};

	return (
		<>
			<fieldset>
				<legend>{t('_general:NUTRIENTS')}</legend>{' '}
				{getValueScopeLabel(ValueScope.HundredGrams, props.characteristic)}
				<br />
				{isIngredientEditor && (
					<>
						<label className="input-label">{t('_general:ADD_NUTRIENT')}</label>
						<div className="input-group mb-3">
							<SuggestionSearchInput
								handleChangeSearchTerm={handleChangeSearchTerm}
								setSavedItem={addNutrientToIngredient}
								triggerSearchLetterAmount={0}
								suggestedItems={suggestedItems}
							/>
						</div>
					</>
				)}
				<div className="row">
					{getNutrientCategoryGroups().map((group: NutrientCategoryGroup, i: number) => (
						<div className="col-6" key={i} style={{ paddingTop: '24px' }}>
							<fieldset
								className="fieldset-white"
								style={{
									height: '700px',
									overflowY: 'auto',
								}}
							>
								<legend>{group.name}</legend>
								{group.nutrients.map((nutrient: Nutrient, i: number) => (
									<div key={i}>{renderInputNutrientValues(nutrient)}</div>
								))}
							</fieldset>
						</div>
					))}
				</div>
			</fieldset>
		</>
	);
};

export default IngredientNutrients;
